import { useState } from "react";
import {
  User,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
  MultiFactorError,
  getMultiFactorResolver,
  MultiFactorResolver
} from "firebase/auth";
import { getAnalytics, setUserId, setUserProperties } from "firebase/analytics";

import { auth } from "../api/firebaseApi";
import {
  identifyMixpanelUser,
  track,
  MIXPANEL_EVENTS
} from "../services/analytics/mixpanelClient";

export const useLogin = () => {
  const [isLoadingLogin, setIsLoadingLogin] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loginAs, setLoginAs] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [errorLogin, setErrorLogin] = useState<string | null>(null);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(
    null
  );

  const onSubmitLogin = async (
    email: string,
    password: string,
    customToken = null,
    userRole = null
  ) => {
    setIsLoadingLogin(true);
    setErrorLogin(null);
    try {
      if (customToken) {
        setLoginAs(true);
        // Ensure we never briefly operate as the previous user during an
        // impersonation/magic-link custom-token swap.
        await signOut(auth);
        const { user } = await signInWithCustomToken(auth, customToken);
        setUserRole(userRole);
        setUser(user);
        // Identify user in Mixpanel after successful custom token login
        identifyMixpanelUser(user.uid, user.email ?? undefined);
      } else {
        const { user } = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );
        setUser(user);
        const analytics = getAnalytics();
        setUserId(analytics, user.uid);
        let isInternal = false;
        if (user.email?.includes("@caseblink.com")) {
          isInternal = true;
        }
        setUserProperties(analytics, { is_internal: isInternal });
        // Identify user in Mixpanel after successful email/password login
        identifyMixpanelUser(user.uid, user.email ?? undefined);
      }
      track(MIXPANEL_EVENTS.LOGIN_SUCCESS);
    } catch (error: any) {
      if (error.code === "auth/multi-factor-auth-required") {
        const resolver = getMultiFactorResolver(
          auth,
          error as MultiFactorError
        );
        setMfaResolver(resolver);
        throw { ...error, resolver };
      } else {
        setErrorLogin(error.code);
        throw error;
      }
    } finally {
      setIsLoadingLogin(false);
    }
  };

  const handleMFASuccess = (user: User) => {
    setUser(user);
    const analytics = getAnalytics();
    setUserId(analytics, user.uid);
    identifyMixpanelUser(user.uid, user.email ?? undefined);
    let isInternal = false;
    if (user.email?.includes("@caseblink.com")) {
      isInternal = true;
    }
    setUserProperties(analytics, { is_internal: isInternal });
  };

  return {
    isLoadingLogin,
    onSubmitLogin,
    user,
    errorLogin,
    loginAs,
    userRole,
    mfaResolver,
    handleMFASuccess
  };
};
